import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './client';
import type {
  QueryNotificationDto,
  NotificationsResponse,
  UnreadCountResponse,
  MentionUsersResponse,
} from '../types/notification';

const NOTIFICATIONS_QUERY_KEY = 'notifications';
const UNREAD_COUNT_QUERY_KEY = 'notifications-unread-count';
const MENTION_USERS_QUERY_KEY = 'mention-users';

// Polling interval: 30 seconds
const POLLING_INTERVAL = 30000;

/**
 * Fetch notifications for the current user
 */
export function useNotifications(query: QueryNotificationDto = {}) {
  return useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, query],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query.isRead !== undefined) params.set('isRead', String(query.isRead));
      if (query.page) params.set('page', String(query.page));
      if (query.limit) params.set('limit', String(query.limit));

      const response = await api.get<NotificationsResponse>(
        `/notifications?${params.toString()}`,
      );
      return response.data;
    },
  });
}

/**
 * Fetch unread notification count with polling
 */
export function useNotificationUnreadCount() {
  return useQuery({
    queryKey: [UNREAD_COUNT_QUERY_KEY],
    queryFn: async () => {
      const response = await api.get<UnreadCountResponse>('/notifications/unread-count');
      return response.data;
    },
    refetchInterval: POLLING_INTERVAL,
    staleTime: POLLING_INTERVAL - 5000, // Consider stale slightly before next poll
  });
}

/**
 * Fetch users for @mention autocomplete
 */
export function useMentionUsers() {
  return useQuery({
    queryKey: [MENTION_USERS_QUERY_KEY],
    queryFn: async () => {
      const response = await api.get<MentionUsersResponse>('/notifications/users');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Mark a single notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await api.patch(`/notifications/${notificationId}/read`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate both lists and count
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
    },
  });
}

/**
 * Mark all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post('/notifications/mark-all-read');
      return response.data;
    },
    onSuccess: () => {
      // Invalidate both lists and count
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
    },
  });
}
